import get from 'lodash/get';
import biLogger from '@wix/web-bi-logger';
import ddt from '@wix/da-ddt';
import { PublicSessionState } from '@wix/da-react-context/pkg/publicSession';
import { getReferrer, commitReferrer } from './referrer';
import type { BiEvent } from './events/base';

type BIContextData = PublicSessionState & {
  view?: string;
  component?: string;
};

export interface BiDefaultOptions {
  version: string;
  platform: string;
  /** To allow migration to a new BI catalog (2024). Always 82 */
  src: number;
  url?: string;
  referer?: string;
  client_id?: string;
  uuid?: string;
  requestid?: string;
  view?: string;
  component?: string;
}

export class BiLogger {
  private defaultOptions: BiDefaultOptions = {
    version: 'eclipse;major',
    platform: 'desktop',
    src: 82,
  };

  getLogger(contextData?: BIContextData) {
    if (typeof window !== 'undefined') {
      this.defaultOptions.url = window.location.toString();

      if (document.referrer) {
        this.defaultOptions.referer = document.referrer;
      }
    }

    if (contextData) {
      this.defaultOptions.platform = contextData.isMobile ? 'mobile' : 'desktop';

      // Try to detect pwa (progressive web app) platform
      if (
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        (window.matchMedia('(display-mode: standalone)').matches ||
          get(window, 'navigator.standalone') === true)
      ) {
        this.defaultOptions.platform = 'pwa';
      }

      if (contextData.biClientId) {
        this.defaultOptions.client_id = contextData.biClientId;
      }

      if (contextData.useridUuid) {
        this.defaultOptions.uuid = contextData.useridUuid;
      }

      this.defaultOptions.requestid = contextData.requestId;
      this.defaultOptions.view = contextData.view;
      this.defaultOptions.component = contextData.component;
    }

    return biLogger
      .factory({ endpoint: 'da-client', trackSession: false })
      .updateDefaults(this.defaultOptions)
      .logger();
  }

  log(
    contextData: BIContextData,
    {
      _biLoggerVersion, // drop any internal use fields
      _value,
      _rankIndex,
      ...eventData
    }: BiEvent
  ): Promise<void> {
    // we are not supposed to send client events from server
    if (typeof window === 'undefined') {
      ddt.error('bilogger', 'Attempted to send client event in SSR, fix me now', eventData);
      return Promise.resolve();
    }

    // if a new pageview, time to save referrer
    if (eventData.evid === 99) {
      commitReferrer();
    }

    const myLogger = this.getLogger(contextData);
    const biData = {
      ...this.defaultOptions,
      referer: getReferrer(),
      ...eventData,
    };
    const logData = biData;

    // you may need to instruct your browser to preserve logs to be able to
    // see these messages
    ddt.log('bilogger', `(evid: ${logData.evid})`, logData, JSON.stringify(logData));

    // we are not supposed to send events from dev
    if (process.env.NODE_ENV !== 'production') {
      return Promise.resolve();
    }

    return myLogger.log(logData);
  }
}

const logger = new BiLogger();
export default logger;
